import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

interface Permission {
  name: string;
  resource: string;
  action: string;
  description?: string;
}

interface AuthorizationContextType {
  permissions: Permission[];
  hasPermission: (permission: string | string[], mode?: 'any' | 'all') => boolean;
  hasResource: (resource: string, action: string) => boolean;
  canAccess: (requiredPermissions: string | string[], options?: AccessOptions) => boolean;
  checkResourceOwnership: (resourceType: string, resourceId: string | number) => Promise<boolean>;
  isLoading: boolean;
  refreshPermissions: () => Promise<void>;
  filterData: <T extends Record<string, any>>(data: T[], filterRules: Record<string, string>) => T[];
}

interface AccessOptions {
  mode?: 'any' | 'all';
  allowSuperAdmin?: boolean;
  requireOwnership?: boolean;
  resourceType?: string;
  resourceId?: string | number;
}

const AuthorizationContext = createContext<AuthorizationContextType | undefined>(undefined);

/**
 * Enhanced Authorization Provider with Security Best Practices
 * Implements client-side permission checking with server validation
 */
export const AuthorizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, token } = useAuth();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [permissionCache, setPermissionCache] = useState<Map<string, { data: any; timestamp: number }>>(new Map());

  // Cache TTL: 5 minutes
  const CACHE_TTL = 5 * 60 * 1000;

  /**
   * Fetch user permissions from server
   */
  const fetchPermissions = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      setPermissions([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.get('/auth/permissions');
      if (response.data?.success) {
        const userPermissions = response.data.data || [];
        setPermissions(userPermissions);
        
        // Cache the permissions
        setPermissionCache(prev => new Map(prev).set(
          `permissions:${user.id}`,
          { data: userPermissions, timestamp: Date.now() }
        ));
      }
    } catch (error) {
      console.error('Failed to fetch user permissions:', error);
      // Fallback to empty permissions for security
      setPermissions([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user?.id]);

  /**
   * Check if user has specific permission(s)
   */
  const hasPermission = useCallback((permission: string | string[], mode: 'any' | 'all' = 'any'): boolean => {
    if (!isAuthenticated || !user) return false;
    
    // Super admin has all permissions (but still check for security)
    if (user.role === 'super_admin') return true;

    if (!permissions || permissions.length === 0) return false;

    const permissionNames = permissions.map(p => p.name);
    const requiredPermissions = Array.isArray(permission) ? permission : [permission];

    if (mode === 'all') {
      return requiredPermissions.every(perm => permissionNames.includes(perm));
    } else {
      return requiredPermissions.some(perm => permissionNames.includes(perm));
    }
  }, [isAuthenticated, user, permissions]);

  /**
   * Check if user has permission for specific resource and action
   */
  const hasResource = useCallback((resource: string, action: string): boolean => {
    if (!isAuthenticated || !user) return false;
    
    if (user.role === 'super_admin') return true;

    return permissions.some(p => p.resource === resource && p.action === action);
  }, [isAuthenticated, user, permissions]);

  /**
   * Enhanced access control with multiple options
   */
  const canAccess = useCallback((
    requiredPermissions: string | string[], 
    options: AccessOptions = {}
  ): boolean => {
    const {
      mode = 'any',
      allowSuperAdmin = true,
      requireOwnership = false
    } = options;

    if (!isAuthenticated || !user) return false;

    // Super admin bypass
    if (allowSuperAdmin && user.role === 'super_admin') return true;

    // Check basic permissions
    const hasBasicPermission = hasPermission(requiredPermissions, mode);
    
    if (!hasBasicPermission) return false;

    // Note: Resource ownership validation should be done server-side
    // This is just a client-side hint and cannot be trusted for security
    if (requireOwnership) {
      console.warn('Resource ownership validation should be performed server-side');
    }

    return hasBasicPermission;
  }, [isAuthenticated, user, hasPermission]);

  /**
   * Check resource ownership (client-side hint only)
   * SECURITY NOTE: This should NOT be relied upon for access control
   */
  const checkResourceOwnership = useCallback(async (
    resourceType: string, 
    resourceId: string | number
  ): Promise<boolean> => {
    if (!isAuthenticated || !user) return false;

    try {
      const cacheKey = `ownership:${resourceType}:${resourceId}:${user.id}`;
      const cached = permissionCache.get(cacheKey);
      
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        return cached.data;
      }

      const response = await api.get(`/auth/check-ownership/${resourceType}/${resourceId}`);
      const hasOwnership = response.data?.success && response.data?.hasOwnership;

      // Cache the result
      setPermissionCache(prev => new Map(prev).set(cacheKey, {
        data: hasOwnership,
        timestamp: Date.now()
      }));

      return hasOwnership;
    } catch (error) {
      console.error('Ownership check failed:', error);
      return false;
    }
  }, [isAuthenticated, user, permissionCache]);

  /**
   * Refresh permissions (e.g., after role change)
   */
  const refreshPermissions = useCallback(async () => {
    // Clear cache
    setPermissionCache(new Map());
    await fetchPermissions();
  }, [fetchPermissions]);

  /**
   * Filter data based on user permissions
   */
  const filterData = useCallback(<T extends Record<string, any>>(
    data: T[], 
    filterRules: Record<string, string>
  ): T[] => {
    if (!isAuthenticated || !data || !Array.isArray(data)) return [];

    return data.map(item => {
      const filteredItem = { ...item };
      
      Object.keys(filterRules).forEach(field => {
        const requiredPermission = filterRules[field];
        if (!hasPermission(requiredPermission)) {
          delete filteredItem[field];
        }
      });

      return filteredItem;
    });
  }, [isAuthenticated, hasPermission]);

  // Fetch permissions when user changes
  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Clean up cache periodically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      setPermissionCache(prev => {
        const newCache = new Map();
        prev.forEach((value, key) => {
          if (now - value.timestamp < CACHE_TTL) {
            newCache.set(key, value);
          }
        });
        return newCache;
      });
    }, 60000); // Clean every minute

    return () => clearInterval(cleanupInterval);
  }, []);

  const value: AuthorizationContextType = {
    permissions,
    hasPermission,
    hasResource,
    canAccess,
    checkResourceOwnership,
    isLoading,
    refreshPermissions,
    filterData
  };

  return (
    <AuthorizationContext.Provider value={value}>
      {children}
    </AuthorizationContext.Provider>
  );
};

/**
 * Hook to use authorization context
 */
export const useAuthorization = (): AuthorizationContextType => {
  const context = useContext(AuthorizationContext);
  if (context === undefined) {
    throw new Error('useAuthorization must be used within an AuthorizationProvider');
  }
  return context;
};

/**
 * Higher-order component for permission-based rendering
 */
export const withPermission = <P extends object>(
  Component: React.ComponentType<P>,
  requiredPermissions: string | string[],
  options: AccessOptions = {}
) => {
  return (props: P) => {
    const { canAccess } = useAuthorization();
    
    if (!canAccess(requiredPermissions, options)) {
      return null;
    }
    
    return <Component {...props} />;
  };
};

/**
 * Component for conditional rendering based on permissions
 */
interface PermissionGateProps {
  permissions: string | string[];
  mode?: 'any' | 'all';
  fallback?: React.ReactNode;
  loading?: React.ReactNode;
  children: React.ReactNode;
}

export const PermissionGate: React.FC<PermissionGateProps> = ({
  permissions,
  mode = 'any',
  fallback = null,
  loading = null,
  children
}) => {
  const { hasPermission, isLoading } = useAuthorization();

  if (isLoading) {
    return <>{loading}</>;
  }

  if (!hasPermission(permissions, mode)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

export default AuthorizationContext;